import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

/**
 * Light/dark theme.
 *
 * A manual choice always beats the OS setting: prefers-color-scheme is read
 * only the first time a visitor arrives with nothing stored, as the starting
 * point — not re-applied on every render, so someone who picks "light" on a
 * dark-mode phone stays in light mode even after the OS setting changes.
 *
 * Applied as a data-theme attribute on <html> rather than a class, matching
 * the [data-theme='dark'] block in index.css. main.jsx sets this attribute
 * before React mounts (reading the same storage key this module reads) so
 * there is no flash of the wrong theme on load.
 */

const STORAGE_KEY = 'gp.theme'

function systemPrefersDark() {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
}

export function initialTheme() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark') return stored
  } catch {
    // Private browsing / storage blocked — fall through to the OS preference.
  }

  return systemPrefersDark() ? 'dark' : 'light'
}

const ThemeContext = createContext(null)

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(initialTheme)

  const setTheme = useCallback((next) => {
    if (next !== 'light' && next !== 'dark') return

    setThemeState(next)

    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Theme still applies for this session even if it can't be remembered.
    }
  }, [])

  const toggle = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark')
  }, [theme, setTheme])

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
  }, [theme])

  const value = useMemo(() => ({ theme, setTheme, toggle }), [theme, setTheme, toggle])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')

  return ctx
}
